import { MessageType } from './message';

/**
 * Minimum drag distance in pixels required to trigger drawer state changes
 * Used to differentiate between taps and intentional drags
 */
export const DRAG_THRESHOLD_PX = 50;

/**
 * Maximum drag distance in % of viewport height for minimized and open states
 */
export const MINIMIZED_DRAG_MAX = 40;
export const OPEN_DRAG_MAX = 70;

/**
 * Time in milliseconds to wait before considering a touch a drag rather than a tap
 */
export const TAP_DURATION_MS = 200;

/**
 * Resting heights. ⚠ Mixed units: OPEN and CLOSED are % of viewport height; MINIMIZED is a CSS
 * length used as-is. See .claude/context/drawer-layout.md and drawer-gestures.md.
 */
export const DRAWER_HEIGHTS = {
  OPEN: 100,
  MINIMIZED: '90px',
  CLOSED: 0
};

export const NR = {
  LOAD: 'MAA_LOADER_LOAD',
  OPEN: 'MAA_LOADER_OPEN',
  CLOSE: 'MAA_LOADER_CLOSE',
  LOADER_TIME: 'MAA_LOADER_TIME'
};

export const USER_QUESTION_CHARACTER_LIMIT = 250;

export const keyPressMap = { Enter: false, Shift: false };

export const FALLBACK_SESSION_ID = 'fallback-sessionId';
export const FALLBACK_USER_ID = 'fallback-userId';

export const MESSAGE_ORIGIN_PREPOPULATED = 'prepopulated';
export const MESSAGE_ORIGIN_CUSTOM = 'custom';
export const MESSAGE_ORIGIN_UNSPECIFIED = 'unspecified';
export const MESSAGE_ORIGIN_RETRY = 'retry';

export const SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY = 'magic apron';
export const IMAGE_UPLOAD_ANALYTICS_EVENT_NAME = 'maa search';
export const ASK_A_QUESTION_ANALYTICS_PLACEHOLDER = 'ask a question clicked';
export const FEEDBACK_ANALYTICS_MAA_PRIMARY_CATEGORY = 'magic apron';
export const FEEDBACK_ANALYTICS_MAA_EVENT_NAME_PREFIX = 'maa feedback';
export const FEEDBACK_ANALYTICS_QNA_PRIMARY_CATEGORY = 'magic apron';
export const FEEDBACK_ANALYTICS_QNA_EVENT_NAME_PREFIX = 'ma qna feedback';
export const FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_MAA = 'maa';
export const FEEDBACK_CLICK_COMMENT_QUESTION_ORIGIN_QNA = 'ma qna';
export const FEEDBACK_CLICK_UPLOAD_CAMERA_ATTACHMENT = 'camera upload';
export const FEEDBACK_CLICK_UPLOAD_FILE_ATTACHMENT = 'file upload';
export const FEEDBACK_CLICK_UPLOAD_IMAGE_ATTACHMENT = 'image upload';
// LIVE_AGENT_ANALYTICS_* live in liveAgent/constants.js. LIVE_AGENT_*_MESSAGE live here: the
// flows and message-utils also write them. See .claude/context/live-agent.md

export const RETRY_MESSAGE = {
  type: MessageType.BOT,
  content: {
    responseSummary: {
      summaryAnswer: 'The conversation was interrupted before I could finish finding the answer.',
    },
  },
};

// Live Agent system-message payloads (see the live-agent.md pointer above).
export const AGENT_HANDOFF_KEYWORDS = ['agent', 'associate'];

export const LIVE_AGENT_SEED_MESSAGE_LIMIT = 10 * 2;

export const LIVE_AGENT_CONNECTING_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'connecting',
  content: {
    text: 'Connecting to a live associate',
  }
};

export const LIVE_AGENT_CONNECTED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'connected',
  content: {
    text: 'You\'re chatting with an associate',
  },
  dividerText: 'Connected',
};

export const LIVE_AGENT_ENDED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'ended',
  content: {
    text: 'You\'re chatting with Magic Apron',
  },
  dividerText: 'Live chat ended',
};

export const LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'idle-ended',
  content: {
    text: 'You\'re chatting with Magic Apron',
  },
  dividerText: 'Live chat ended due to inactivity',
};

export const LIVE_AGENT_ERROR_MESSAGE = {
  type: MessageType.SYSTEM,
  variant: 'agent-error',
  content: {
    text: 'You\'re chatting with Magic Apron',
    error: 'We were unable to connect you with a live associate.',
  },
  dividerText: 'Live chat ended'
};

export const VALID_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
export const MAX_FILE_SIZE_MB = 20;

export const UPLOAD_ERROR_MESSAGES = {
  UNSUPPORTED_CONTENT_PROPRIETY: 'This image doesn\'t meet content guidelines. Select a different image and try again.',
  FILE_SIZE_EXCEEDED: `This file is too large. Choose a file ${MAX_FILE_SIZE_MB} MB or less and try again.`,
  UNSUPPORTED_FILE_TYPE: 'This file type is not supported. Please upload a valid file.',
  GENERIC_ERROR: 'Something went wrong with the upload. Try again later.'
};

export const DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH = 400;
export const IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION = 0.8;
export const IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION = 0.5;

/** The greeting row's copy, and the contextual landing's heading when its call answers without one. */
export const GREETING_COPY = 'What can I help you with today?';

/** One shows per bot answer. BotLoadingMessage adds the animated ellipsis, so the copy has none. */
export const BOT_LOADING_MESSAGES = [
  'Measuring twice',
  'Checking my tool belt',
  'Consulting the blueprint',
  'Sketching out the plan',
  'Framing up an answer',
  'Nailing down the details',
  'Building this from the ground up',
  'Laying the foundation for your answer',
  'Adding the final coat',
];
